import React, { useState } from 'react';
import {
  FiHelpCircle,
  FiMail,
  FiPhone,
  FiMessageSquare,
  FiBook,
  FiVideo,
  FiDownload,
  FiExternalLink,
  FiChevronDown,
  FiChevronUp,
  FiSearch,
  FiUser,
  FiSettings,
  FiShield,
  FiCreditCard,
  FiUsers,
  FiFileText,
  FiTool,
  FiLifeBuoy
} from 'react-icons/fi';
import './Help.css';

function Help() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const categories = [
    { id: 'all', name: 'All Topics', icon: <FiBook /> },
    { id: 'getting-started', name: 'Getting Started', icon: <FiUser /> },
    { id: 'email-campaigns', name: 'Email Campaigns', icon: <FiMail /> },
    { id: 'email-builder', name: 'Email Builder', icon: <FiTool /> },
    { id: 'templates', name: 'Templates & Library', icon: <FiFileText /> },
    { id: 'team', name: 'Team & Roles', icon: <FiUsers /> },
    { id: 'analytics', name: 'Analytics & Reports', icon: <FiSettings /> },
    { id: 'account', name: 'Account Settings', icon: <FiSettings /> },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: <FiTool /> }
  ];

  const faqData = [
    // Getting Started
    {
      id: 1,
      category: 'getting-started',
      question: 'How do I create my first email campaign?',
      answer: 'Navigate to the Email Campaigns page and click "Create Campaign". Choose your campaign type, select a template or start from scratch, and follow the step-by-step campaign builder to create and send your first email.'
    },
    {
      id: 2,
      category: 'getting-started',
      question: 'How do I invite team members to my account?',
      answer: 'Go to Team Management and click "Invite Member". Enter their email address, assign a role (Super Admin, Admin, Member), and send the invitation. They\'ll receive an email with instructions to join your team.'
    },
    {
      id: 3,
      category: 'getting-started',
      question: 'What is the difference between Projects and Library?',
      answer: 'Projects are your active email templates and campaigns that you\'re working on. Library contains shared templates, assets, and resources that can be reused across your team.'
    },
    
    // Email Campaigns
    {
      id: 4,
      category: 'email-campaigns',
      question: 'How do I schedule an email campaign?',
      answer: 'When creating a campaign, choose "Schedule" instead of "Send Now". Set your desired date and time, and the campaign will be automatically sent at the scheduled time.'
    },
    {
      id: 5,
      category: 'email-campaigns',
      question: 'Can I track email opens and clicks?',
      answer: 'Yes! All campaigns automatically track opens and clicks. You can view detailed analytics in the Campaign Analytics section, including open rates, click rates, and recipient engagement.'
    },
    {
      id: 6,
      category: 'email-campaigns',
      question: 'How do I add recipients to my campaign?',
      answer: 'In the campaign builder, go to the Recipients step. You can add individual email addresses, import from a CSV file, or select from your existing contact lists.'
    },
    {
      id: 7,
      category: 'email-campaigns',
      question: 'What happens if my scheduled campaign fails to send?',
      answer: 'Failed campaigns are automatically retried. You can also manually retry failed sends from the Campaigns page. Check the Activity log for detailed error information.'
    },
    
    // Email Builder
    {
      id: 8,
      category: 'email-builder',
      question: 'How do I use the drag-and-drop email builder?',
      answer: 'The email builder allows you to drag elements like text blocks, images, buttons, and dividers onto your canvas. Click on any element to edit its content, styling, and properties.'
    },
    {
      id: 9,
      category: 'email-builder',
      question: 'Can I add custom HTML to my emails?',
      answer: 'Yes! You can switch to the HTML editor mode to add custom HTML code, or use the "Custom HTML" element in the drag-and-drop builder for more advanced customization.'
    },
    {
      id: 10,
      category: 'email-builder',
      question: 'How do I make my emails mobile-responsive?',
      answer: 'All templates are automatically mobile-responsive. You can preview how your email looks on different devices using the preview options in the email builder.'
    },
    
    // Templates & Library
    {
      id: 11,
      category: 'templates',
      question: 'How do I save a template to the Library?',
      answer: 'After creating your email in the builder, click "Save as Template" and give it a name. It will be saved to your Library and can be reused for future campaigns.'
    },
    {
      id: 12,
      category: 'templates',
      question: 'Can I share templates with my team?',
      answer: 'Yes! Templates saved to the Library are automatically shared with team members based on their permissions. Team members can view and use shared templates for their campaigns.'
    },
    {
      id: 13,
      category: 'templates',
      question: 'How do I organize my templates?',
      answer: 'You can organize templates by creating folders in the Library, adding tags, or using the search functionality to quickly find specific templates.'
    },
    
    // Team & Roles
    {
      id: 14,
      category: 'team',
      question: 'What are the different user roles and permissions?',
      answer: 'Super Admin: Full system access. Admin: Full team access. Member: Can create campaigns and templates. Each role has specific permissions for viewing, creating, editing, and deleting content.'
    },
    {
      id: 15,
      category: 'team',
      question: 'How do I change a team member\'s role?',
      answer: 'Go to Team Management, find the member you want to update, click "Edit", and select their new role. Changes take effect immediately.'
    },
    {
      id: 16,
      category: 'team',
      question: 'How do I remove a team member?',
      answer: 'In Team Management, find the member and click "Remove". You can also deactivate their account temporarily. Removed members will lose access immediately.'
    },
    {
      id: 17,
      category: 'team',
      question: 'What is Role Management?',
      answer: 'Role Management allows you to create custom roles with specific permissions. You can define what each role can view, create, edit, or delete across different sections of the platform.'
    },
    
    // Analytics & Reports
    {
      id: 18,
      category: 'analytics',
      question: 'How do I view campaign performance?',
      answer: 'Go to the Campaigns page and click on any sent campaign to view its analytics. You\'ll see open rates, click rates, bounce rates, and detailed recipient engagement data.'
    },
    {
      id: 19,
      category: 'analytics',
      question: 'What analytics data is available?',
      answer: 'We track opens, clicks, bounces, unsubscribes, and delivery status. You can also see which links were clicked most, when emails were opened, and recipient engagement patterns.'
    },
    {
      id: 20,
      category: 'analytics',
      question: 'How do I export campaign reports?',
      answer: 'In the campaign analytics view, click "Export Report" to download detailed performance data in CSV format for further analysis.'
    },
    
    // Account Settings
    {
      id: 21,
      category: 'account',
      question: 'How do I change my password?',
      answer: 'Go to Settings > Account Settings. Click "Change Password", enter your current password and new password, then click "Update Password" to save the changes.'
    },
    {
      id: 22,
      category: 'account',
      question: 'How do I update my profile information?',
      answer: 'Navigate to Settings > Profile. You can update your name, email address, profile picture, and other personal information. Click "Save Changes" after making updates.'
    },
    {
      id: 23,
      category: 'account',
      question: 'How do I manage notification settings?',
      answer: 'Go to Settings > Notification Settings to customize which notifications you receive via email and in-app notifications for campaigns, team activities, and system updates.'
    },
    
    // Troubleshooting
    {
      id: 24,
      category: 'troubleshooting',
      question: 'Why can\'t I see my campaigns or templates?',
      answer: 'Check your user permissions and make sure you\'re looking in the right section. If you still can\'t see your content, try refreshing the page or contact your team admin.'
    },
    {
      id: 25,
      category: 'troubleshooting',
      question: 'The email builder is running slowly, what can I do?',
      answer: 'Try clearing your browser cache, closing other browser tabs, or switching to a different browser. Large templates with many images may also cause performance issues.'
    },
    {
      id: 26,
      category: 'troubleshooting',
      question: 'My scheduled campaign didn\'t send, what should I do?',
      answer: 'Check the Activity log for error details. You can manually retry failed campaigns from the Campaigns page. If the issue persists, contact support with the campaign ID.'
    },
    {
      id: 27,
      category: 'troubleshooting',
      question: 'How do I recover a deleted campaign or template?',
      answer: 'Deleted campaigns and templates can be recovered from the Activity log if they were deleted recently. Contact support if you need help recovering older content.'
    }
  ];

  const contactOptions = [
    {
      type: 'Email Support',
      icon: <FiMail />,
      details: 'support@mailmaster.com',
      description: 'Get help via email within 24 hours',
      action: 'mailto:support@mailmaster.com'
    },
    {
      type: 'Live Chat',
      icon: <FiMessageSquare />,
      details: 'Available 9 AM - 6 PM EST',
      description: 'Chat with our support team in real-time',
      action: '#'
    },
    {
      type: 'Phone Support',
      icon: <FiPhone />,
      details: '+1 (555) 123-4567',
      description: 'Call us for urgent issues (Premium plans only)',
      action: 'tel:+15551234567'
    },
    {
      type: 'Help Center',
      icon: <FiBook />,
      details: 'Comprehensive documentation',
      description: 'Browse our detailed guides and tutorials',
      action: '#'
    }
  ];

  const resources = [
    {
      title: 'Email Campaign Guide',
      description: 'Complete guide to creating and managing email campaigns',
      icon: <FiMail />,
      type: 'Guide'
    },
    {
      title: 'Email Builder Tutorial',
      description: 'Learn how to use the drag-and-drop email builder',
      icon: <FiVideo />,
      type: 'Tutorial'
    },
    {
      title: 'Template Library',
      description: 'Browse our collection of professional email templates',
      icon: <FiFileText />,
      type: 'Library'
    },
    {
      title: 'Analytics & Reporting',
      description: 'Understanding campaign performance and metrics',
      icon: <FiSettings />,
      type: 'Guide'
    },
    {
      title: 'Team Management',
      description: 'Setting up roles and managing team permissions',
      icon: <FiUsers />,
      type: 'Guide'
    },
    {
      title: 'API Documentation',
      description: 'Developer resources and API integration guides',
      icon: <FiDownload />,
      type: 'Documentation'
    }
  ];

  const filteredFAQs = faqData.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (faqId) => {
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
  };

  const handleContactClick = (action) => {
    if (action.startsWith('mailto:') || action.startsWith('tel:')) {
      window.location.href = action;
    } else if (action === '#') {
      // Handle live chat or other actions
      alert('This feature will be implemented soon!');
    }
  };

  return (
    <div className="page-main">
      <div className="page-title-row">
        <h1><FiHelpCircle className="page-title-icon" size={28} />{" "}Help & Support</h1>
      </div>

      {/* Hero Section */}
      <div className="help-hero">
        <h2>How can we help you today?</h2>
        <p>Find answers about email campaigns, team management, analytics, and more. Browse our resources or get in touch with our support team.</p>
        
        <div className="help-search">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search for campaigns, templates, team management, analytics..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Quick Contact Options */}
      <div className="contact-options">
        <h3>Get Support</h3>
        <div className="contact-grid">
          {contactOptions.map((option, index) => (
            <div 
              key={index} 
              className="contact-card"
              onClick={() => handleContactClick(option.action)}
            >
              <div className="contact-icon">
                {option.icon}
              </div>
              <div className="contact-content">
                <h4>{option.type}</h4>
                <p className="contact-details">{option.details}</p>
                <p className="contact-description">{option.description}</p>
              </div>
              <FiExternalLink className="contact-arrow" />
            </div>
          ))}
        </div>
      </div>

      {/* Resources Section */}
      <div className="resources-section">
        <h3>Helpful Resources</h3>
        <div className="resources-grid">
          {resources.map((resource, index) => (
            <div key={index} className="resource-card">
              <div className="resource-icon">
                {resource.icon}
              </div>
              <div className="resource-content">
                <h4>{resource.title}</h4>
                <p>{resource.description}</p>
                <span className="resource-type">{resource.type}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="faq-section">
        <h3>Frequently Asked Questions</h3>
        
        {/* Category Filter */}
        <div className="category-filter">
          {categories.map(category => (
            <button
              key={category.id}
              className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.icon}
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="faq-list">
          {filteredFAQs.length === 0 ? (
            <div className="no-results">
              <FiSearch className="no-results-icon" />
              <h4>No results found</h4>
              <p>Try adjusting your search terms or browse different categories.</p>
            </div>
          ) : (
            filteredFAQs.map(faq => (
              <div key={faq.id} className="faq-item">
                <div 
                  className="faq-question"
                  onClick={() => toggleFAQ(faq.id)}
                >
                  <h4>{faq.question}</h4>
                  {expandedFAQ === faq.id ? <FiChevronUp /> : <FiChevronDown />}
                </div>
                {expandedFAQ === faq.id && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Still Need Help Section */}
      <div className="need-help-section">
        <div className="need-help-card">
          <FiLifeBuoy className="need-help-icon" />
          <div className="need-help-content">
            <h3>Still need help?</h3>
            <p>Can't find what you're looking for? Our support team is here to help you succeed.</p>
            <div className="need-help-actions">
              <button 
                className="btn-primary"
                onClick={() => handleContactClick('mailto:support@mailmaster.com')}
              >
                <FiMail />
                Contact Support
              </button>
              <button 
                className="btn-secondary"
                onClick={() => handleContactClick('#')}
              >
                <FiMessageSquare />
                Start Live Chat
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Help;
